// ============================================================
// Raya ERP landing page — content + data model
// Ported from the Raya Website Redesign design compose.
// ============================================================

// [fg, bg] pairs keyed by semantic tone.
export const TONES = {
  ok: ['var(--raya-success)', 'var(--raya-success-bg)'],
  warn: ['var(--raya-warning)', 'var(--raya-warning-bg)'],
  info: ['var(--raya-info)', 'var(--raya-info-bg)'],
  err: ['var(--raya-error)', 'var(--raya-error-bg)'],
  blue: ['var(--raya-blue-600)', '#eff6ff'],
  slate: ['var(--raya-slate-500)', 'var(--raya-slate-100)'],
};

// ─── Block builders (mirror the design's screen-block helpers) ───────────────
const cellNorm = (c) => {
  if (typeof c === 'string') c = { t: c };
  const tone = c.tone ? TONES[c.tone] : null;
  return {
    text: c.t,
    align: c.a || 'left',
    weight: c.b ? 600 : 400,
    family: c.m ? 'var(--font-mono)' : 'var(--font-app)',
    color: tone && !c.chip ? tone[0] : 'var(--raya-slate-700)',
    isChip: !!c.chip,
    chipColor: tone ? tone[0] : '',
    chipBg: tone ? tone[1] : '',
  };
};

export const bFields = (items) => ({
  type: 'fields',
  items: items.map(([label, value, m]) => ({
    label,
    value,
    family: m ? 'var(--font-mono)' : 'var(--font-app)',
  })),
});

export const bTable = (grid, cols, rows) => ({
  type: 'table',
  grid,
  cols: cols.map((c) => (Array.isArray(c) ? { t: c[0], align: c[1] } : { t: c, align: 'left' })),
  rows: rows.map((r) => ({ cells: r.map(cellNorm) })),
});

export const bKpis = (items) => ({
  type: 'kpis',
  items: items.map(([label, value, tone]) => ({
    label,
    value,
    color: tone ? TONES[tone][0] : 'var(--raya-slate-900)',
    bg: tone ? TONES[tone][1] : 'var(--raya-slate-50)',
    border: tone ? 'transparent' : 'var(--raya-slate-200)',
  })),
});

export const bProg = (items) => ({
  type: 'progress',
  items: items.map(([label, pct, note, tone]) => ({
    label,
    pct: pct + '%',
    note,
    color: TONES[tone || 'blue'][0],
  })),
});

export const bChips = (items) => ({
  type: 'chips',
  items: items.map(([text, tone]) => ({ text, color: TONES[tone][0], bg: TONES[tone][1] })),
});

export const bTotal = (label, value) => ({ type: 'total', label, value });
export const bNote = (text) => ({ type: 'note', text });

// ─── Deep module views (accordion + live panel) ──────────────────────────────
export const MODULES_DEEP = [
  { key: 'inv', name: 'Inventory', icon: 'package', color: '#2563eb', tint: '#eff6ff',
    crumb: 'Inventory › Items › Stock summary', badge: ['Live', 'ok'],
    desc: 'Multi-warehouse stock with batch and serial tracking, allocated the moment an order lands.',
    points: ['Batch, serial and expiry tracking', 'Reorder alerts before stock-outs', 'Transfers between warehouses'] },
  { key: 'mfg', name: 'Manufacturing', icon: 'factory', color: '#0891b2', tint: '#ecfeff',
    crumb: 'Manufacturing › Work orders › WO-2210', badge: ['Running', 'info'],
    desc: 'BOM to finished goods — work orders scheduled to the line, material drawn automatically, QC before stock.',
    points: ['BOM with routing and scrap rates', 'Stage-wise shop-floor tracking', 'Costing rolled up per unit'] },
  { key: 'pur', name: 'Purchase', icon: 'shopping-cart', color: '#d97706', tint: '#fffbeb',
    crumb: 'Purchase › Orders › PO-3391', badge: ['Matched', 'ok'],
    desc: 'Indent to payment, with PO, GRN and vendor bill matched three ways before a rupee leaves.',
    points: ['Auto-indent from reorder points', 'GRN with short-supply flags', 'Three-way match on every bill'] },
  { key: 'crm', name: 'CRM & Marketing', icon: 'megaphone', color: '#4f46e5', tint: '#eef2ff',
    crumb: 'CRM › Pipeline', badge: ['This quarter', 'info'],
    desc: 'A pipeline that knows your stock and your rates, so quotes convert straight into orders.',
    points: ['Quotes priced from live stock', 'Campaign to enquiry to order tracking', 'Follow-ups that never slip'] },
  { key: 'hr', name: 'HR & Payroll', icon: 'contact-round', color: '#e11d48', tint: '#fff1f2',
    crumb: 'HR › Payroll › August 2026', badge: ['Processing', 'info'],
    desc: 'Attendance, shifts, leave and salary runs on the same org that runs your operations.',
    points: ['Biometric and mobile attendance', 'Shifts tied to work orders', 'PF, ESI and PT computed for you'] },
  { key: 'fin', name: 'Finance & Reports', icon: 'landmark', color: '#059669', tint: '#ecfdf5',
    crumb: 'Finance › Overview', badge: ['Reconciled', 'ok'],
    desc: 'Ledgers, receivables and margins built from the same entries your team already made.',
    points: ['Bank feed auto-matching', 'Costing pulled from production', 'GSTR-1 / 3B ready exports'] },
  { key: 'pos', name: 'Point of Sale', icon: 'store', color: '#ea580c', tint: '#fff7ed',
    crumb: 'POS › Manager view › Today', badge: ['Live', 'ok'], link: '/point-of-sale',
    desc: 'A counter app for retail shops that bills from the same stock and prices, and posts every bill to the books instantly.',
    points: ['Scan-first or touch-tile counters', 'Cash, UPI, card and credit on one bill', 'Shifts, Z-reports and GSTR-1 ready sales'] },
  { key: 'rep', name: 'Dashboards', icon: 'bar-chart-3', color: '#0d9488', tint: '#f0fdfa',
    crumb: 'Reports › Sales overview', badge: ['Live data', 'ok'],
    desc: 'One screen for orders, revenue, stock and margins — no exports, no stale spreadsheets.',
    points: ['Dashboards on live records', 'Alerts on the KPIs you choose', 'Drill from chart to document'] },
];

// ─── End-to-end workflows (animated stepper + live screens) ──────────────────
export const WORKFLOWS = [
  {
    name: 'Order to Cash', sub: 'Sales cycle', tag: 'Sales · Inventory · Finance', icon: 'workflow',
    color: '#2563eb', tint: '#eff6ff', rail: 2,
    steps: [
      { icon: 'file-plus-2', label: 'Order comes in', sub: 'From CRM or direct entry',
        screen: { crumb: 'Sales › Orders › New', title: 'Sales Order — SO-1043', badge: ['Draft', 'slate'], blocks: [
          bFields([['Customer', 'Sunrise Polymers'], ['GSTIN', '24AABCS4521K1Z8', true], ['Order date', '01 Aug 2026'], ['Payment terms', 'Net 30 days']]),
          bTable('2fr 0.8fr 0.8fr 1fr', ['Item', 'HSN', 'Qty', ['Amount', 'right']], [
            [{ t: 'HDPE Granules — Grade A', b: 1 }, { t: '3901', m: 1 }, '12 MT', { t: '₹3,24,000', b: 1, a: 'right' }],
            [{ t: 'LDPE Film Roll', b: 1 }, { t: '3920', m: 1 }, '4 MT', { t: '₹1,58,000', b: 1, a: 'right' }],
          ]),
          bTotal('Total incl. GST 18%', '₹5,68,760'),
        ] } },
      { icon: 'package-check', label: 'Stock allocated', sub: 'Warehouse & batch reserved',
        screen: { crumb: 'Inventory › Allocation', title: 'Stock Allocation — SO-1043', badge: ['Reserved', 'ok'], blocks: [
          bTable('1.7fr 1.1fr 0.9fr 1fr', ['Item', 'Warehouse', 'Batch', ['Status', 'right']], [
            [{ t: 'HDPE Granules', b: 1 }, 'Vatva WH-1', { t: 'B-2261', m: 1 }, { t: 'Allocated', chip: 1, tone: 'ok', a: 'right' }],
            [{ t: 'LDPE Film Roll', b: 1 }, 'Vatva WH-2', { t: 'B-2248', m: 1 }, { t: 'Allocated', chip: 1, tone: 'ok', a: 'right' }],
          ]),
          bKpis([['Reserved', '16 MT'], ['Free after', '42 MT'], ['Below reorder', '2 items', 'warn']]),
          bNote('Allocation happens the moment the order is saved — no separate stock request.'),
        ] } },
      { icon: 'receipt-indian-rupee', label: 'Invoice + e-Way Bill', sub: 'IRN & QR in one step',
        screen: { crumb: 'Sales › Invoicing', title: 'Tax Invoice — INV-2043', badge: ['IRN generated', 'ok'], blocks: [
          bFields([['IRN', 'a4f8…2c4e6d8b', true], ['e-Way Bill', 'EWB-8812 4405 7723', true], ['Invoice value', '₹5,68,760'], ['IRP status', 'Acknowledged']]),
          bChips([['QR embedded on invoice', 'ok'], ['e-Way Bill valid 2 days', 'info'], ['Posted to GSTR-1 data', 'info'], ['HSN validated', 'info']]),
          bNote('Invoice, IRN, QR and e-Way Bill are produced in a single action.'),
        ] } },
      { icon: 'truck', label: 'Goods dispatched', sub: 'Delivery note attached',
        screen: { crumb: 'Operations › Dispatch', title: 'Delivery Note — DN-1043', badge: ['In transit', 'info'], blocks: [
          bFields([['Transporter', 'Shree Maruti Logistics'], ['Vehicle', 'GJ-01-KT-4482', true], ['LR number', 'SML-77213', true], ['ETA', '03 Aug 2026']]),
          bProg([['Packed', 100, '2 pallets', 'ok'], ['Loaded', 100, 'Gate pass issued', 'ok'], ['In transit', 46, 'Near Vadodara'], ['Delivered', 0, 'Awaiting POD', 'slate']]),
        ] } },
      { icon: 'indian-rupee', label: 'Payment received', sub: 'Ledger reconciled',
        screen: { crumb: 'Finance › Receipts', title: 'Receipt — RCP-0977', badge: ['Paid', 'ok'], blocks: [
          bKpis([['Amount received', '₹5,68,760', 'ok'], ['Mode', 'NEFT'], ['Days to pay', '21']]),
          bFields([['Bank', 'HDFC ****4421'], ['Matched to', 'INV-2043'], ['Ledger', 'Sunrise Polymers'], ['Posted on', '01 Aug 2026']]),
          bChips([['Ledger reconciled', 'ok'], ['Order closed', 'info'], ['GSTR-1 / 3B ready', 'info']]),
        ] } },
    ],
  },
  {
    name: 'Procure to Pay', sub: 'PO – GRN cycle', tag: 'Purchase · Inventory · Finance', icon: 'shopping-cart',
    color: '#d97706', tint: '#fffbeb', rail: 1,
    steps: [
      { icon: 'clipboard-list', label: 'Indent raised', sub: 'Need flagged by any module',
        screen: { crumb: 'Purchase › Indents', title: 'Purchase Indent — PI-0412', badge: ['Approved', 'ok'], blocks: [
          bNote('Auto-raised: HDPE Granules fell below its 40 MT reorder point.'),
          bTable('1.9fr 0.8fr 1fr 1.1fr', ['Material', 'Qty', 'Required by', ['Raised by', 'right']], [
            [{ t: 'HDPE Granules — Grade A', b: 1 }, '30 MT', '08 Aug 2026', { t: 'Stores · auto', a: 'right' }],
            [{ t: 'Masterbatch — Blue', b: 1 }, '400 kg', '08 Aug 2026', { t: 'Production', a: 'right' }],
          ]),
          bKpis([['Est. value', '₹9.8L'], ['Approval', '1 level'], ['Vendors invited', '3']]),
        ] } },
      { icon: 'file-text', label: 'PO issued', sub: 'Vendor, rates & terms',
        screen: { crumb: 'Purchase › Orders', title: 'Purchase Order — PO-3391', badge: ['Sent to vendor', 'info'], blocks: [
          bFields([['Vendor', 'Gujarat Polychem'], ['GSTIN', '24AACG7781L1ZP', true], ['Deliver to', 'Vatva WH-1'], ['Terms', 'Net 45 days']]),
          bTable('1.9fr 0.8fr 1fr 1fr', ['Material', 'Qty', 'Rate', ['Amount', 'right']], [
            [{ t: 'HDPE Granules — Grade A', b: 1 }, '30 MT', '₹27,600 / MT', { t: '₹8,28,000', b: 1, a: 'right' }],
            [{ t: 'Masterbatch — Blue', b: 1 }, '400 kg', '₹265 / kg', { t: '₹1,06,000', b: 1, a: 'right' }],
          ]),
          bTotal('PO value incl. GST', '₹11,02,120'),
        ] } },
      { icon: 'package-open', label: 'GRN posted', sub: 'Goods received & counted',
        screen: { crumb: 'Purchase › Goods receipt', title: 'Goods Receipt — GRN-1187', badge: ['Posted', 'ok'], blocks: [
          bTable('1.6fr 0.8fr 0.9fr 0.9fr 1fr', ['Material', 'Ordered', 'Received', 'Accepted', ['Status', 'right']], [
            [{ t: 'HDPE Granules', b: 1 }, '30 MT', '29.5 MT', '29.5 MT', { t: 'Short 0.5', chip: 1, tone: 'warn', a: 'right' }],
            [{ t: 'Masterbatch', b: 1 }, '400 kg', '400 kg', '400 kg', { t: 'Complete', chip: 1, tone: 'ok', a: 'right' }],
          ]),
          bKpis([['Received value', '₹9,20,700'], ['Short supply', '0.5 MT', 'warn'], ['Stock updated', 'Instantly', 'ok']]),
          bNote('Stock, vendor ledger and the PO balance all update from this one entry.'),
        ] } },
      { icon: 'badge-check', label: 'QC check', sub: 'Accept, reject or hold',
        screen: { crumb: 'Quality › Inspection', title: 'QC Report — QC-0441', badge: ['Passed', 'ok'], blocks: [
          bFields([['Batch', 'B-2318', true], ['Sample size', '6 bags'], ['Moisture', '0.04% — within spec'], ['Melt flow index', '7.2 g/10 min']]),
          bProg([['Visual inspection', 100, 'No contamination', 'ok'], ['Lab test', 100, 'Within tolerance', 'ok'], ['Approval', 100, 'Signed by QC head', 'ok']]),
          bChips([['Batch accepted into stores', 'ok'], ['Certificate attached', 'info']]),
        ] } },
      { icon: 'wallet', label: 'Bill & payment', sub: 'Three-way matched',
        screen: { crumb: 'Finance › Vendor bills', title: 'Vendor Bill — VB-2094', badge: ['Three-way matched', 'ok'], blocks: [
          bTable('1.4fr 1fr 1.1fr 1fr', ['Document', 'Qty', 'Value', ['Match', 'right']], [
            [{ t: 'PO-3391', b: 1, m: 1 }, '30 MT', '₹11,02,120', { t: 'Base', chip: 1, tone: 'slate', a: 'right' }],
            [{ t: 'GRN-1187', b: 1, m: 1 }, '29.5 MT', '₹10,84,180', { t: 'Matched', chip: 1, tone: 'ok', a: 'right' }],
            [{ t: 'VB-2094', b: 1, m: 1 }, '29.5 MT', '₹10,84,180', { t: 'Matched', chip: 1, tone: 'ok', a: 'right' }],
          ]),
          bChips([['Matched automatically', 'ok'], ['Payment scheduled 15 Sep', 'info'], ['ITC eligible', 'info']]),
        ] } },
    ],
  },
  {
    name: 'Plan to Produce', sub: 'Manufacturing cycle', tag: 'Manufacturing · Inventory · QC', icon: 'factory',
    color: '#0891b2', tint: '#ecfeff', rail: 4,
    steps: [
      { icon: 'layers', label: 'BOM defined', sub: 'Materials & routing',
        screen: { crumb: 'Manufacturing › BOM', title: 'BOM-77 — 50 L HDPE Drum', badge: ['Active', 'ok'], blocks: [
          bTable('1.8fr 1fr 1.1fr 1fr', ['Component', 'Per unit', 'Source', ['In stock', 'right']], [
            [{ t: 'HDPE Granules — Grade A', b: 1 }, '2.4 kg', 'Vatva WH-1', { t: '86 MT', a: 'right' }],
            [{ t: 'Masterbatch — Blue', b: 1 }, '40 g', 'Vatva WH-1', { t: '640 kg', a: 'right' }],
            [{ t: 'Lid + gasket set', b: 1 }, '1 set', 'Odhav Depot', { t: '4,200 sets', a: 'right' }],
          ]),
          bNote('Every work order draws material against this BOM automatically.'),
        ] } },
      { icon: 'clipboard-check', label: 'Work order', sub: 'Scheduled to the line',
        screen: { crumb: 'Manufacturing › Work orders', title: 'Work Order — WO-2210', badge: ['Scheduled', 'info'], blocks: [
          bFields([['Product', '50 L HDPE Drum'], ['Quantity', '2,000 units'], ['Line', 'Blow moulding — Line 2'], ['Due', '06 Aug 2026']]),
          bProg([['Line capacity booked', 72, '2 shifts / day'], ['Material availability', 100, 'All components in stock', 'ok']]),
          bKpis([['Std. cost / unit', '₹214'], ['Planned hours', '46 h'], ['Operators', '8']]),
        ] } },
      { icon: 'package-minus', label: 'Material issued', sub: 'Auto-drawn from stores',
        screen: { crumb: 'Stores › Material issue', title: 'Issue Slip — MI-1902', badge: ['Issued', 'ok'], blocks: [
          bTable('1.8fr 1fr 1fr 1fr', ['Material', 'Required', 'Issued', ['Balance', 'right']], [
            [{ t: 'HDPE Granules', b: 1 }, '4.8 MT', '4.8 MT', { t: '81.2 MT', a: 'right' }],
            [{ t: 'Masterbatch — Blue', b: 1 }, '80 kg', '80 kg', { t: '560 kg', a: 'right' }],
            [{ t: 'Lid + gasket set', b: 1 }, '2,000', '2,000', { t: '2,200', a: 'right' }],
          ]),
          bKpis([['Issued value', '₹4,86,200'], ['Stores stock after', '81 MT'], ['Variance', '0%', 'ok']]),
        ] } },
      { icon: 'cog', label: 'Production run', sub: 'Stage-wise tracking',
        screen: { crumb: 'Manufacturing › Production', title: 'WO-2210 — Live run', badge: ['Running', 'info'], blocks: [
          bProg([['Extrusion', 100, 'Completed', 'ok'], ['Blow moulding', 68, '1,360 of 2,000 units'], ['Trimming & finishing', 24, 'In progress'], ['Packing', 0, 'Not started', 'slate']]),
          bKpis([['Produced', '1,360 units'], ['Scrap', '1.2%', 'warn'], ['On schedule', 'Yes', 'ok']]),
          bNote('Shop-floor updates post straight to stock and costing — no end-of-day reconciliation.'),
        ] } },
      { icon: 'badge-check', label: 'QC & finish', sub: 'Finished goods to stock',
        screen: { crumb: 'Quality › Finished goods', title: 'FG Inspection — FG-2210', badge: ['Cleared', 'ok'], blocks: [
          bKpis([['Accepted', '1,344 units', 'ok'], ['Rejected', '16 units', 'err'], ['Yield', '98.8%']]),
          bChips([['Moved to FG store', 'ok'], ['Batch FG-2210 created', 'info'], ['Cost rolled into finance', 'info']]),
          bNote('Finished stock is sellable the moment QC clears it.'),
        ] } },
    ],
  },
];

// ─── Static section content ──────────────────────────────────────────────────
export const RAIL_ITEMS = [
  { icon: 'home', color: '#93c5fd', bg: 'rgba(96,165,250,0.18)' },
  { icon: 'package', color: 'rgba(255,255,255,0.6)', bg: 'transparent' },
  { icon: 'shopping-cart', color: 'rgba(255,255,255,0.6)', bg: 'transparent' },
  { icon: 'line-chart', color: 'rgba(255,255,255,0.6)', bg: 'transparent' },
  { icon: 'factory', color: 'rgba(255,255,255,0.6)', bg: 'transparent' },
  { icon: 'landmark', color: 'rgba(255,255,255,0.6)', bg: 'transparent' },
  { icon: 'users', color: 'rgba(255,255,255,0.6)', bg: 'transparent' },
  { icon: 'bar-chart-3', color: 'rgba(255,255,255,0.6)', bg: 'transparent' },
];

export const HUB_TILES = [
  { icon: 'package', name: 'Inventory', desc: 'Items & stock levels', chip: '9 items', color: '#2563eb', tint: '#eff6ff' },
  { icon: 'shopping-cart', name: 'Purchase', desc: 'PO, GRN & bills', chip: '1 pending', color: '#d97706', tint: '#fffbeb' },
  { icon: 'factory', name: 'Manufacturing', desc: 'BOM & work orders', chip: '2 running', color: '#0891b2', tint: '#ecfeff' },
  { icon: 'landmark', name: 'Finance', desc: 'Ledger & P&L', chip: 'GSTR ready', color: '#7c3aed', tint: '#f5f3ff' },
];

export const STATEMENTS = [
  { figure: '8', title: 'Modules, one database', desc: 'Stock, orders, production, people and books read the same records.' },
  { figure: '0', title: 'Plugins for GST', desc: 'IRN, QR and e-Way Bill come from the platform itself, not an add-on.' },
  { figure: '1', title: 'Entry per transaction', desc: 'An order typed once flows to stock, invoice, dispatch and ledger.' },
];

export const MODULES_GRID = [
  { icon: 'package', name: 'Inventory', desc: 'Multi-warehouse stock, batches, reorder points.', color: '#2563eb', tint: '#eff6ff' },
  { icon: 'receipt-indian-rupee', name: 'Sales & Invoicing', desc: 'Quote to invoice with IRN + QR on the spot.', color: '#7c3aed', tint: '#f5f3ff' },
  { icon: 'shopping-cart', name: 'Purchase', desc: 'Indent, PO, GRN and vendor bills, connected.', color: '#d97706', tint: '#fffbeb' },
  { icon: 'factory', name: 'Manufacturing', desc: 'BOM, work orders, QC and production tracking.', color: '#0891b2', tint: '#ecfeff' },
  { icon: 'landmark', name: 'Finance', desc: 'Ledgers, reconciliation and P&L in real time.', color: '#059669', tint: '#ecfdf5' },
  { icon: 'contact-round', name: 'HR & Payroll', desc: 'Attendance, leave, shifts and salary runs.', color: '#e11d48', tint: '#fff1f2' },
  { icon: 'megaphone', name: 'CRM & Marketing', desc: 'Leads, campaigns, follow-ups and quotes.', color: '#4f46e5', tint: '#eef2ff' },
  { icon: 'bar-chart-3', name: 'Reports', desc: 'Dashboards on live data. GSTR-ready exports.', color: '#0d9488', tint: '#f0fdfa' },
];

// Products with their own page, shown as a row under the modules grid.
// Add one line here when a new product page ships.
export const PRODUCT_PAGES = [
  { icon: 'store', name: 'Point of Sale', desc: 'Counter billing that posts every bill to stock and the books.', href: '/point-of-sale', color: '#ea580c', tint: '#fff7ed' },
  { icon: 'shopping-bag', name: 'Online Store', desc: 'Your branded web store, selling from live ERP stock.', href: '/online-store', color: '#7c3aed', tint: '#f5f3ff', tag: 'New' },
  { icon: 'folder-kanban', name: 'Project Management', desc: 'Boards, timesheets and a P&L for every project.', href: '/project-management', color: '#0891b2', tint: '#ecfeff' },
  { icon: 'truck', name: 'Fleet Management', desc: 'Trips, freight bills, lorry-hire TDS and fuel cards.', href: '/fleet-management', color: '#16a34a', tint: '#f0fdf4' },
];

export const PIPELINE = [
  { stage: 'Leads', pct: '100%', value: '₹48.2L', color: '#c7d2fe' },
  { stage: 'Qualified', pct: '72%', value: '₹34.6L', color: '#a5b4fc' },
  { stage: 'Quoted', pct: '46%', value: '₹22.1L', color: '#818cf8' },
  { stage: 'Won', pct: '28%', value: '₹13.4L', color: '#4f46e5' },
];

const BAR_DATA = [
  ['46%', 'Mon', '₹1.1L'], ['62%', 'Tue', '₹1.5L'], ['54%', 'Wed', '₹1.3L'],
  ['72%', 'Thu', '₹1.8L'], ['84%', 'Fri', '₹2.1L'], ['66%', 'Sat', '₹1.6L'], ['96%', 'Sun', '₹2.4L'],
];
export const CHART_BARS = BAR_DATA.map(([h, day, val], i) => ({ h, day, val, color: i >= 4 ? '#0d9488' : '#99f6e4' }));

export const COMPLIANCE_CHIPS = [
  'Multi-GSTIN, single PAN', 'Direct IRP integration', 'IRN + QR on every invoice',
  'e-Way Bill generation', 'GSTR-1 / 3B ready data', 'HSN / SAC masters',
];

export const HOW_STEPS = [
  { num: '1', title: 'Create your account', desc: 'Pick your industry and company size. The experience is tailored for you.' },
  { num: '2', title: 'Set up your modules', desc: 'Enable inventory, sales, manufacturing, HR — whatever your business needs.' },
  { num: '3', title: 'Invite your team', desc: 'Add users with role-based access. Everyone sees exactly what they need.' },
  { num: '4', title: 'Start operating', desc: 'Run your entire business from one dashboard. That simple.' },
];

export const INDUSTRIES = [
  { icon: 'factory', name: 'Manufacturing', desc: 'BOM, work orders, quality control and production tracking.' },
  { icon: 'container', name: 'Trading', desc: 'Multi-warehouse stock, purchase orders and supply chain.' },
  { icon: 'truck', name: 'Logistics & Delivery', desc: 'Vehicle register, hired-truck bills with TDS, e-Way Bills and driver payroll.', link: '/fleet-management', linkLabel: 'See Fleet Management' },
  { icon: 'gem', name: 'Jewelry', desc: 'Karigar management, stone inventory and custom orders.' },
  { icon: 'stethoscope', name: 'Healthcare', desc: 'Medical inventory, patient billing and procurement.' },
  { icon: 'store', name: 'Retail', desc: 'Raya POS at the counter, stock across stores and GST-ready books.', link: '/point-of-sale', linkLabel: 'See Raya POS' },
  { icon: 'wrench', name: 'Services', desc: 'Projects on boards, timesheets with rates and a margin for every job.', link: '/project-management', linkLabel: 'See Project Management' },
];

export const SECURITY = [
  { icon: 'shield-check', title: 'Enterprise security', desc: 'SOC 2 compliant infrastructure with end-to-end encryption.' },
  { icon: 'key-round', title: 'MFA & role-based access', desc: 'Multi-factor authentication and granular permissions per user.' },
  { icon: 'refresh-cw', title: 'Always improving', desc: 'Weekly updates and a roadmap shaped by customer feedback.' },
];

export const FAQS = [
  { q: 'What is Raya ERP?', a: 'Raya ERP is an all-in-one ERP & CRM platform that brings inventory, sales, purchase, manufacturing, finance and HR into a single place for Indian businesses.' },
  { q: 'Does Raya ERP support GST and e-Invoicing?', a: 'Yes. IRN and QR are generated on every invoice via the IRP, e-Way Bills are created in the same step, and your GSTR-1 / 3B data stays filing-ready.' },
  { q: 'Does Raya ERP handle manufacturing?', a: 'Yes — BOM, work orders, material issue, stage-wise production tracking and quality control, with finished goods flowing back into inventory automatically.' },
  { q: 'Can it run purchase, PO and GRN?', a: 'Yes. Indents can be auto-raised from reorder points, POs go out with vendor rates and terms, GRNs record short supply, and vendor bills are matched three ways before payment.' },
  { q: 'Which industries does Raya ERP support?', a: 'Manufacturing, Trading, Logistics & Delivery, Jewelry, Healthcare, Retail and Services — each with tailored workflows for that vertical.' },
  { q: 'Does Raya ERP have a point of sale for retail counters?', a: 'Yes. Raya POS is a counter app for retail shops that sells from the same products, stock, prices and GST as Raya ERP and posts every bill to the books instantly. Switch it on from Settings › General and bill from a PC, Android tablet or iPad in the browser.' },
  { q: 'How do I get started?', a: 'Create your account, pick your industry, enable the modules you need, invite your team with role-based access, and start operating from one dashboard.' },
];
